/**
 * Sidebar component for the tutor feature
 * Shows conversation history and new chat button
 * Styled to match MessagesSidebar
 */
import Button from "@shared/components/ui/Button";
import { PlusIcon } from "@shared/components/ui/Icon";
import theme from "@shared/utils/themeColors";
import { formatDistanceToNow } from 'date-fns';
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { TutorConversation } from "../types";

interface TutorSidebarProps {
  conversations: TutorConversation[];
  currentConversationId?: string;
  loading: boolean;
  onNewChat: () => void;
  onDeleteConversation?: (id: string) => Promise<void>;
  sidebarOpen?: boolean;
  setSidebarOpen?: (open: boolean) => void;
}

const TutorSidebar: React.FC<TutorSidebarProps> = ({
  conversations,
  currentConversationId,
  loading,
  onNewChat,
  onDeleteConversation,
  sidebarOpen = true,
  setSidebarOpen = () => {},
}) => {
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  return (
    <div
      id="tutor-sidebar"
      className={`absolute z-20 top-0 bottom-0 w-full md:w-auto md:static md:top-auto md:bottom-auto -mr-px md:translate-x-0 transition-transform duration-200 ease-in-out ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div
        className={`sticky top-16 bg-white dark:bg-[#151D2C] overflow-x-hidden overflow-y-auto no-scrollbar shrink-0 border-r ${theme.border.primary} md:w-[18rem] xl:w-[20rem] h-[calc(100dvh-64px)]`}
      >
        {/* Header and New Chat Button */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${theme.text.primary}`}>
              Tutor Chats
            </h2>
            <Button onClick={onNewChat} leftIcon={<PlusIcon />}>
              New chat
            </Button>
          </div>

          {/* Conversation List */}
          <div className="mt-4">
            <div className={`text-xs font-semibold ${theme.text.muted}`}>
              Recent conversations
            </div>
            <ul className="mb-6 mt-2">
              {loading ? (
                <div className="flex items-center justify-center h-20">
                  <div
                    className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.border.brand}`}
                  ></div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center">
                  <p className={theme.text.muted}>No conversations yet</p>
                  <p className={theme.text.muted}>Start a new chat to begin</p>
                </div>
              ) : (
                conversations.map((conversation) => (
                  <li className="-mx-2" key={conversation.id}>
                    <div className="flex items-center w-full">
                      <Link
                        to={`/tutor/${conversation.id}`}
                        className={`flex-grow flex items-center p-2 rounded-lg ${
                          currentConversationId === conversation.id
                            ? `bg-[linear-gradient(135deg,var(--tw-gradient-stops))] ${theme.bg.gradientPrimary}`
                            : "hover:bg-slate-50 dark:hover:bg-slate-800"
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <div className="flex flex-col truncate">
                          <span
                            className={`text-sm font-medium truncate ${theme.text.primary}`}
                          >
                            {conversation.title}
                          </span>
                          <span className={`text-xs ${theme.text.muted}`}>
                            {formatDistanceToNow(
                              new Date(conversation.updatedAt),
                              { addSuffix: true }
                            )}
                          </span>
                        </div>
                      </Link>
                      {confirmDelete === conversation.id ? (
                        <div className="flex items-center space-x-1 px-1">
                          <button
                            onClick={() => {
                              if (onDeleteConversation) {
                                onDeleteConversation(conversation.id);
                              }
                              setConfirmDelete(null);
                            }}
                            className="text-xs p-1 text-red-500 hover:text-red-700"
                            title="Confirm delete"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="text-xs p-1 text-gray-500 hover:text-gray-700"
                            title="Cancel"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(conversation.id);
                          }}
                          className="p-2 text-gray-400 hover:text-red-500"
                          title="Delete conversation"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorSidebar;
